import { Autocomplete, InputAdornment, TextField } from '@mui/material'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import { FC, useEffect, useState } from 'react'
import { useGetList, useNotify, useTranslate } from 'react-admin'
import { useLocation, useNavigate } from 'react-router-dom'
import { CERTIFICATE_ORGANIZATION_SWITCH_SCOPE } from '../config'
import { useHasScope } from '../hooks/useHasScope'
import useSwitchOrganization from '../hooks/useSwitchOrganization'
import type { OrganizationRecord } from '../types/apiTypes'
import { border } from './theme/border'
import { themeColor } from './theme/color'

// Organization switcher in the header; only for users with the switch scope.
const OrganizationSwitcher: FC = () => {
  const canSwitchOrg = useHasScope(CERTIFICATE_ORGANIZATION_SWITCH_SCOPE)
  return canSwitchOrg ? <Switcher /> : null
}

const Switcher: FC = () => {
  const translate = useTranslate()
  const switchOrganization = useSwitchOrganization()
  const notify = useNotify()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [selectedOrg, setSelectedOrg] = useState(localStorage.getItem('org_id_override') || '')
  const [selectedOrgName, setSelectedOrgName] = useState(localStorage.getItem('org_name_override') || '')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Keep the selection in sync when it is changed elsewhere (e.g. after creating an organization).
  useEffect(() => {
    const sync = () => {
      setSelectedOrg(localStorage.getItem('org_id_override') || '')
      setSelectedOrgName(localStorage.getItem('org_name_override') || '')
    }
    window.addEventListener('orgChange', sync)
    return () => window.removeEventListener('orgChange', sync)
  }, [])

  // Debounce so not every keystroke hits the API.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Server-side search: the list can hold more organizations than one page.
  const { data: organizationsData, isFetching } = useGetList<OrganizationRecord>(
    'organization',
    { pagination: { page: 1, perPage: 50 }, sort: { field: 'name', order: 'ASC' }, filter: { q: debouncedSearch } },
    { staleTime: 60_000 }
  )

  // The selected org may not be in the search results: look up its name by id so the display is never empty.
  const { data: selectedOrgData } = useGetList<OrganizationRecord>(
    'organization',
    { pagination: { page: 1, perPage: 5 }, sort: { field: 'name', order: 'ASC' }, filter: { q: selectedOrg } },
    { enabled: !!selectedOrg && !selectedOrgName, staleTime: 60_000 }
  )
  const resolvedName = selectedOrgName ||
    selectedOrgData?.find(org => org.organizationId === selectedOrg)?.name ||
    selectedOrg
  type OrgOption = Pick<OrganizationRecord, 'organizationId' | 'name'>
  const selectedOption: OrgOption | null = selectedOrg ? { organizationId: selectedOrg, name: resolvedName } : null
  const organizations: OrgOption[] = organizationsData ?? []

  return (
    <Autocomplete
      size="small"
      sx={{ width: { xs: 180, sm: 260 }, mr: 1 }}
      options={selectedOption && !organizations.some(org => org.organizationId === selectedOrg)
        ? [selectedOption, ...organizations]
        : organizations}
      value={selectedOption}
      loading={isFetching}
      filterOptions={options => options}
      getOptionLabel={org => org.name}
      isOptionEqualToValue={(org, value) => org.organizationId === value.organizationId}
      onInputChange={(_event, value, reason) => {
        if (reason === 'input') setSearch(value)
        if (reason === 'reset' || reason === 'clear') setSearch('')
      }}
      onChange={(_event, org) => {
        if (!org) return
        setSelectedOrg(org.organizationId)
        setSelectedOrgName(org.name)
        // A record page (/certificate/<id>/show) belongs to the previous organization: back to its
        // list first, so the refresh doesn't refetch that record under the new organization.
        const [resource, id] = pathname.split('/').filter(Boolean)
        if (resource && id && id !== 'create') void navigate(`/${resource}`)
        switchOrganization(org.organizationId, org.name)
        notify('data.organizationSwitched', { type: 'info', messageArgs: { name: org.name } })
      }}
      renderOption={(props, org) => <li {...props} key={org.organizationId}>{org.name}</li>}
      renderInput={params => (
        <TextField
          {...params}
          placeholder={translate('data.selectOrganization')}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                <BusinessOutlinedIcon sx={{ fontSize: 18, color: themeColor.slate50 }} />
              </InputAdornment>
            ),
            sx: { 'fontSize': 13.5, 'bgcolor': themeColor.slate05, '& fieldset': { borderColor: border.light } },
          }}
        />
      )}
    />
  )
}

export default OrganizationSwitcher
